/**
 * FAQ page (templates/page.faq.json)
 *
 * - Gom tất cả section [data-faq-category] trên trang để dựng menu danh mục bên trái.
 * - Trong cùng 1 thời điểm chỉ có 1 danh mục (section) được hiển thị.
 * - Khi chuyển danh mục, trang cuộn lên đầu và accordion đầu tiên của danh mục đó luôn được mở.
 * - Accordion đóng/mở có animation trượt (Web Animations API); trong 1 danh mục
 *   chỉ có 1 câu hỏi được mở tại 1 thời điểm.
 * - Styling dùng Tailwind CDN (đăng ký trong layout/theme.liquid).
 */
class FAQPage {
  constructor() {
    this.categories = Array.from(document.querySelectorAll('[data-faq-category]'));
    if (this.categories.length === 0) return;

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.#buildMenus();
    this.#bindAccordions();
    this.#activate(this.#initialCategory(), false);
  }

  activateBySectionId(sectionId) {
    const category = this.#findById(sectionId);
    if (category) this.#activate(category, false);
  }

  openBlock(sectionId, blockId) {
    const category = this.#findById(sectionId);
    if (!category) return;

    this.#activate(category, false);
    const accordion = category.querySelector(`details[data-block-id="${blockId}"]`);
    if (accordion) accordion.open = true;
  }

  #findById(sectionId) {
    return this.categories.find((category) => category.dataset.faqId === sectionId);
  }

  #initialCategory() {
    const hash = decodeURIComponent(window.location.hash.replace('#', ''));
    return this.categories.find((category) => category.dataset.faqHandle === hash) || this.categories[0];
  }

  #buildMenus() {
    const items = this.categories.map((category) => ({
      id: category.dataset.faqId,
      title: category.dataset.faqTitle,
      iconTpl: category.querySelector('template[data-faq-icon-tpl]'),
    }));

    // Mỗi danh mục có 1 nav riêng, đều chứa đầy đủ menu của tất cả danh mục
    for (const category of this.categories) {
      const menu = category.querySelector('[data-faq-menu]');
      if (!menu) continue;

      menu.textContent = '';
      const list = document.createElement('ul');
      list.className = 'flex flex-col divide-y divide-black/10';

      for (const item of items) {
        list.appendChild(this.#buildMenuItem(item));
      }

      menu.appendChild(list);
    }
  }

  #buildMenuItem(item) {
    const listItem = document.createElement('li');

    const button = document.createElement('button');
    button.type = 'button';
    button.dataset.faqMenuTarget = item.id;
    button.className =
      'flex w-full items-center gap-3 px-4 py-3 text-left text-[12px] uppercase tracking-wider transition-colors hover:bg-black/5';

    if (item.iconTpl) {
      const icon = document.createElement('span');
      icon.className = 'flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-current';
      icon.setAttribute('aria-hidden', 'true');
      icon.appendChild(item.iconTpl.content.cloneNode(true));
      button.appendChild(icon);
    }

    const label = document.createElement('span');
    label.textContent = item.title;
    button.appendChild(label);

    button.addEventListener('click', () => {
      const target = this.#findById(item.id);
      if (target) this.#activate(target, true);
    });

    listItem.appendChild(button);
    return listItem;
  }

  #activate(category, fromMenuClick) {
    for (const other of this.categories) {
      other.classList.toggle('is-active', other === category);
    }

    this.#markActiveMenuItems(category);
    this.#openFirstAccordion(category);

    if (fromMenuClick) {
      if (category.dataset.faqHandle) {
        history.replaceState(null, '', `#${category.dataset.faqHandle}`);
      }

      window.scrollTo({ top: 0, behavior: this.reducedMotion ? 'auto' : 'smooth' });
    }
  }

  #markActiveMenuItems(category) {
    for (const button of document.querySelectorAll('[data-faq-menu-target]')) {
      const isActive = button.dataset.faqMenuTarget === category.dataset.faqId;
      button.classList.toggle('font-bold', isActive);
      button.classList.toggle('bg-black/5', isActive);
    }
  }

  #openFirstAccordion(category) {
    let isFirst = true;

    // Chuyển danh mục: không cần animation, đặt trạng thái trực tiếp
    for (const accordion of category.querySelectorAll('details.faq-accordion')) {
      this.#cancelAnimation(accordion);
      accordion.open = isFirst;
      isFirst = false;
    }
  }

  #bindAccordions() {
    for (const category of this.categories) {
      for (const accordion of category.querySelectorAll('details.faq-accordion')) {
        const summary = accordion.querySelector('summary');
        if (!summary) continue;

        summary.addEventListener('click', (event) => {
          event.preventDefault();
          this.#toggleAccordion(accordion, category);
        });
      }
    }
  }

  #toggleAccordion(accordion, category) {
    const isClosing = accordion.classList.contains('is-closing');

    if (accordion.open && !isClosing) {
      this.#closeAccordion(accordion);
      return;
    }

    // Chỉ 1 câu hỏi mở tại 1 thời điểm trong cùng danh mục
    for (const other of category.querySelectorAll('details.faq-accordion[open]')) {
      if (other !== accordion) this.#closeAccordion(other);
    }

    this.#openAccordion(accordion);
  }

  #openAccordion(accordion) {
    const answer = accordion.querySelector('[data-faq-answer]');
    const startHeight = accordion.classList.contains('is-closing') && answer ? answer.getBoundingClientRect().height : 0;

    this.#cancelAnimation(accordion);
    accordion.open = true;

    if (!answer || this.reducedMotion) return;

    answer.animate(
      { height: [`${startHeight}px`, `${answer.scrollHeight}px`], opacity: [startHeight > 0 ? 1 : 0, 1] },
      { duration: 300, easing: 'ease-out' }
    );
  }

  #closeAccordion(accordion) {
    const answer = accordion.querySelector('[data-faq-answer]');

    if (!answer || this.reducedMotion) {
      accordion.open = false;
      return;
    }

    const startHeight = answer.getBoundingClientRect().height;
    this.#cancelAnimation(accordion);
    accordion.classList.add('is-closing');

    const animation = answer.animate(
      { height: [`${startHeight}px`, '0px'], opacity: [1, 0] },
      { duration: 250, easing: 'ease-in' }
    );

    animation.onfinish = () => {
      accordion.open = false;
      accordion.classList.remove('is-closing');
    };
  }

  #cancelAnimation(accordion) {
    accordion.classList.remove('is-closing');
    const answer = accordion.querySelector('[data-faq-answer]');
    if (!answer) return;

    for (const animation of answer.getAnimations()) {
      animation.cancel();
    }
  }
}

(() => {
  let faqPage = new FAQPage();

  // Hỗ trợ theme editor: section được render lại / chọn section hoặc block
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', () => {
      faqPage = new FAQPage();
    });

    document.addEventListener('shopify:section:select', (event) => {
      faqPage.activateBySectionId(event.detail.sectionId);
    });

    document.addEventListener('shopify:block:select', (event) => {
      faqPage.openBlock(event.detail.sectionId, event.detail.blockId);
    });
  }
})();
